import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../core/common/header/PageHeader";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaUser,
  FaEnvelope,
  FaBook,
  FaFileUpload,
  FaHome,
  FaSync,
  FaDownload,
  FaFilter,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

interface RegulationDto {
  id: number;
  enrollmentId: number;
  courseTitle: string;
  status: string;
  uploadedFilePath: string;
  uploadedFileDownloadUrl: string;
  uploadedAt: string;
  reviewedAt?: string;
  studentName: string;
  studentEmail: string;
  reviewedByName?: string;
  rejectionReason?: string;
}

type TabType = "pending" | "accepted" | "rejected";

const AdminRegulationReview: React.FC = () => {
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_BASE_URL;

  const [activeTab, setActiveTab] = useState<TabType>("pending");
  const [pendingRegulations, setPendingRegulations] = useState<RegulationDto[]>(
    []
  );
  const [allRegulations, setAllRegulations] = useState<RegulationDto[]>([]);
  const [filteredRegulations, setFilteredRegulations] = useState<
    RegulationDto[]
  >([]);

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<number | null>(null);
  const [viewingDocId, setViewingDocId] = useState<number | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [rejectionReason, setRejectionReason] = useState<string>("");
  const [showRejectModal, setShowRejectModal] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Filtering
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCourse, setSelectedCourse] = useState<string>("all");

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    filterAndSortRegulations();
  }, [allRegulations, activeTab, searchTerm, selectedCourse]);

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([loadPendingRegulations(), loadAllRegulations()]);
    setLoading(false);
    setLastUpdated(new Date());
  };

  const loadPendingRegulations = async () => {
    try {
      const response = await fetch(`${API}/api/admin/regulations/pending`, {
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to load pending regulations");

      const data = await response.json();
      setPendingRegulations(data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadAllRegulations = async () => {
    try {
      // You'll need to create this endpoint in the backend
      const response = await fetch(`${API}/api/admin/regulations/all`, {
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to load all regulations");

      const data = await response.json();
      setAllRegulations(data);
    } catch (err) {
      console.error(err);
    }
  };

  const filterAndSortRegulations = () => {
    let filtered = [...allRegulations];

    // Filter by status based on active tab
    if (activeTab === "accepted") {
      filtered = filtered.filter((r) => r.status === "ACCEPTED");
    } else if (activeTab === "rejected") {
      filtered = filtered.filter((r) => r.status === "REJECTED");
    } else {
      filtered = pendingRegulations;
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.studentName.toLowerCase().includes(term) ||
          r.studentEmail.toLowerCase().includes(term) ||
          r.courseTitle.toLowerCase().includes(term)
      );
    }

    // Filter by course
    if (selectedCourse !== "all") {
      filtered = filtered.filter((r) => r.courseTitle === selectedCourse);
    }

    // Sort by date (descending - newest first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.reviewedAt || a.uploadedAt).getTime();
      const dateB = new Date(b.reviewedAt || b.uploadedAt).getTime();
      return dateB - dateA;
    });

    setFilteredRegulations(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const refreshData = async () => {
    setRefreshing(true);
    await loadAllData();
    setRefreshing(false);
  };

  const handleAccept = async (regulationId: number) => {
    if (!window.confirm("Are you sure you want to accept this regulation?")) {
      return;
    }

    try {
      setProcessing(regulationId);
      const response = await fetch(
        `${API}/api/admin/regulations/${regulationId}/review`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "accept" }),
        }
      );

      if (!response.ok) throw new Error("Failed to accept regulation");

      alert("Regulation accepted successfully!");
      await loadAllData();
    } catch (err) {
      console.error(err);
      alert("Failed to accept regulation");
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (regulationId: number) => {
    if (!rejectionReason.trim()) {
      alert("Please provide a rejection reason");
      return;
    }

    try {
      setProcessing(regulationId);
      const response = await fetch(
        `${API}/api/admin/regulations/${regulationId}/review`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "reject",
            rejectionReason: rejectionReason.trim(),
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to reject regulation");

      alert("Regulation rejected. Student can re-upload.");
      setShowRejectModal(null);
      setRejectionReason("");
      await loadAllData();
    } catch (err) {
      console.error(err);
      alert("Failed to reject regulation");
    } finally {
      setProcessing(null);
    }
  };

  const handleViewDocument = async (regulationId: number) => {
    try {
      setViewingDocId(regulationId);
      const response = await fetch(
        `${API}/api/admin/regulations/${regulationId}/download-url`,
        {
          credentials: "include",
        }
      );

      if (!response.ok) throw new Error("Failed to get download URL");

      const data = await response.json();
      window.open(data.downloadUrl, "_blank");
    } catch (err) {
      console.error(err);
      alert("Failed to view document");
    } finally {
      setViewingDocId(null);
    }
  };

  const handleDownload = async (regulationId: number, studentName: string) => {
    try {
      setDownloadingId(regulationId);
      const response = await fetch(
        `${API}/api/admin/regulations/${regulationId}/download-url`,
        {
          credentials: "include",
        }
      );

      if (!response.ok) throw new Error("Failed to get download URL");

      const data = await response.json();

      // Download the file
      const link = document.createElement("a");
      link.href = data.downloadUrl;
      link.download = `${studentName}_regulation.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err);
      alert("Failed to download document");
    } finally {
      setDownloadingId(null);
    }
  };

  // Get unique courses for filter
  const uniqueCourses = Array.from(
    new Set(allRegulations.map((r) => r.courseTitle))
  ).sort();

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRegulations.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredRegulations.length / itemsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009fe3] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading regulations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaFileUpload />}
        title="Course Regulations"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Course Regulations" },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex justify-end mb-4">
          <button
            onClick={() => navigate("/admin/regulation-templates")}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg transition-colors text-sm font-semibold shadow-sm"
          >
            <FaFileUpload className="w-4 h-4" />
            Templates
          </button>
        </div>
        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab("pending")}
                className={`px-6 py-4 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === "pending"
                    ? "border-[#009fe3] text-[#009fe3]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Pending Review ({pendingRegulations.length})
              </button>
              <button
                onClick={() => setActiveTab("accepted")}
                className={`px-6 py-4 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === "accepted"
                    ? "border-[#009fe3] text-[#009fe3]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Accepted
              </button>
              <button
                onClick={() => setActiveTab("rejected")}
                className={`px-6 py-4 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === "rejected"
                    ? "border-[#009fe3] text-[#009fe3]"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Rejected
              </button>
            </nav>
          </div>

          {/* Filters - Only show for accepted/rejected tabs */}
          {activeTab !== "pending" && (
            <div className="p-6 bg-gray-50 border-b border-gray-200">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Student/Course
                  </label>
                  <input
                    type="text"
                    placeholder="Search by name, email, or course..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  />
                </div>
                <div className="w-full md:w-64">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Course
                  </label>
                  <select
                    value={selectedCourse}
                    onChange={(e) => setSelectedCourse(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                  >
                    <option value="all">All Courses</option>
                    {uniqueCourses.map((course) => (
                      <option key={course} value={course}>
                        {course}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Pending Reviews Section */}
        {activeTab === "pending" && (
          <div className="space-y-6">
            {currentItems.length === 0 ? (
              <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                <FaCheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  All Caught Up!
                </h3>
                <p className="text-gray-600">
                  There are no regulations pending review at the moment.
                </p>
              </div>
            ) : (
              currentItems.map((regulation) => (
                <div
                  key={regulation.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                >
                  <div className="p-6">
                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                          <FaUser className="w-4 h-4 text-[#009fe3]" />
                          Student Information
                        </h3>
                        <div className="space-y-2">
                          <p className="text-gray-700">
                            <span className="font-medium">Name:</span>{" "}
                            {regulation.studentName}
                          </p>
                          <p className="text-gray-600 text-sm flex items-center gap-2">
                            <FaEnvelope className="w-3 h-3" />
                            {regulation.studentEmail}
                          </p>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
                          <FaBook className="w-4 h-4 text-[#009fe3]" />
                          Course Information
                        </h3>
                        <p className="text-gray-700 font-medium">
                          {regulation.courseTitle}
                        </p>
                        <p className="text-gray-600 text-sm mt-2">
                          Uploaded on:{" "}
                          {new Date(regulation.uploadedAt).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-3 mb-6 pb-6 border-b border-gray-200">
                      <button
                        onClick={() => handleViewDocument(regulation.id)}
                        disabled={viewingDocId === regulation.id}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all shadow-sm ${
                          viewingDocId === regulation.id
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-[#009fe3] text-white hover:bg-cyan-600 hover:shadow-md"
                        }`}
                      >
                        <FaEye className="w-4 h-4" />
                        {viewingDocId === regulation.id
                          ? "Opening..."
                          : "View Document"}
                      </button>
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => handleAccept(regulation.id)}
                        disabled={processing === regulation.id}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold transition-all shadow-sm ${
                          processing === regulation.id
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-emerald-600 text-white hover:bg-emerald-700 hover:shadow-md"
                        }`}
                      >
                        <FaCheckCircle className="w-4 h-4" />
                        {processing === regulation.id
                          ? "Processing..."
                          : "Accept"}
                      </button>
                      <button
                        onClick={() => setShowRejectModal(regulation.id)}
                        disabled={processing === regulation.id}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold transition-all shadow-sm ${
                          processing === regulation.id
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-rose-600 text-white hover:bg-rose-700 hover:shadow-md"
                        }`}
                      >
                        <FaTimesCircle className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Accepted/Rejected Table View */}
        {(activeTab === "accepted" || activeTab === "rejected") && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {currentItems.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-500">
                  No {activeTab} regulations found.
                  {(searchTerm || selectedCourse !== "all") &&
                    " Try adjusting your filters."}
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Student
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Course
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Reviewed By
                        </th>
                        {activeTab === "rejected" && (
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Reason
                          </th>
                        )}
                        <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentItems.map((regulation) => (
                        <tr key={regulation.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {regulation.studentName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {regulation.studentEmail}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {regulation.courseTitle}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {new Date(
                                regulation.reviewedAt || regulation.uploadedAt
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {regulation.reviewedByName || "—"}
                            </div>
                          </td>
                          {activeTab === "rejected" && (
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900 max-w-xs truncate">
                                {regulation.rejectionReason || "—"}
                              </div>
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() =>
                                  handleViewDocument(regulation.id)
                                }
                                disabled={viewingDocId === regulation.id}
                                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                  viewingDocId === regulation.id
                                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                    : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                                }`}
                                title="View document"
                              >
                                <FaEye className="w-3 h-3" />
                                {viewingDocId === regulation.id
                                  ? "..."
                                  : "View"}
                              </button>
                              <button
                                onClick={() =>
                                  handleDownload(
                                    regulation.id,
                                    regulation.studentName
                                  )
                                }
                                disabled={downloadingId === regulation.id}
                                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                                  downloadingId === regulation.id
                                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                    : "bg-green-100 text-green-700 hover:bg-green-200"
                                }`}
                                title="Download document"
                              >
                                <FaDownload className="w-3 h-3" />
                                {downloadingId === regulation.id
                                  ? "..."
                                  : "Download"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
                    <div className="text-sm text-gray-700">
                      Showing {indexOfFirstItem + 1} to{" "}
                      {Math.min(indexOfLastItem, filteredRegulations.length)} of{" "}
                      {filteredRegulations.length} results
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FaChevronLeft className="w-3 h-3" />
                      </button>

                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                        (page) => (
                          <button
                            key={page}
                            onClick={() => paginate(page)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                              currentPage === page
                                ? "bg-[#009fe3] text-white"
                                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {page}
                          </button>
                        )
                      )}

                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FaChevronRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </main>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Reject Regulation
            </h3>
            <p className="text-gray-600 mb-4">
              Please provide a reason for rejection. The student will see this
              message.
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              rows={4}
            />
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => handleReject(showRejectModal)}
                disabled={
                  !rejectionReason.trim() || processing === showRejectModal
                }
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg font-semibold transition-colors"
              >
                {processing === showRejectModal
                  ? "Processing..."
                  : "Confirm Reject"}
              </button>
              <button
                onClick={() => {
                  setShowRejectModal(null);
                  setRejectionReason("");
                }}
                disabled={processing === showRejectModal}
                className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 text-gray-700 rounded-lg font-semibold transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRegulationReview;
